import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const tries = useRef(0);
  const { user } = useAuth();

  useEffect(() => {
    if (!sid) { nav("/"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setStatus("success");
          // Refresh user so subscription_status is updated client-side
          try { await api.get("/auth/me"); } catch {}
          setTimeout(() => nav("/dashboard", { replace: true }), 1500);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
        tries.current += 1;
        if (tries.current > 15) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        tries.current += 1;
        if (tries.current > 15) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sid, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="payment-success">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin" />
            <h1 className="font-display text-2xl font-extrabold mt-6">Confirming your subscription…</h1>
            <p className="text-sm text-slate-500 mt-2">Please wait while Stripe confirms the payment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="font-display text-3xl font-extrabold mt-6">You're in, {user?.name?.split(" ")[0] || "there"}!</h1>
            <p className="text-sm text-slate-500 mt-2">Quick Quotes Pro is active. Redirecting to your dashboard…</p>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <h1 className="font-display text-2xl font-extrabold mt-6">Payment not confirmed</h1>
            <p className="text-sm text-slate-500 mt-2">If you believe this is a mistake, please try again or contact support.</p>
            <button onClick={() => nav("/pricing")} className="mt-4 text-blue-600 font-medium hover:underline" data-testid="try-again">Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
